import React, { useEffect, useRef } from 'react';
import CanvasElementContent from './CanvasElementContent';

// Wrapper editável de um elemento no canvas: posicionamento absoluto + seleção +
// edição inline de texto (contentEditable). O drag/resize/rotate é aplicado pelo
// <Moveable> do CanvasEditor, que usa o nó registrado em `registerRef`.
export default function CanvasElement({ elemento, proposta, selected, editing, registerRef, onSelect, onStartEdit, onCommitHtml }) {
  const editableRef = useRef(null);

  // Ao entrar em edição, injeta o html atual e foca no fim do texto
  useEffect(() => {
    if (editing && editableRef.current) {
      editableRef.current.innerHTML = elemento.conteudo?.html || '';
      editableRef.current.focus();
      const range = document.createRange();
      range.selectNodeContents(editableRef.current);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const estilo = elemento.estilo || {};
  const isTexto = elemento.tipo === 'texto';
  // Camada trancada no painel de camadas: não recebe clique nenhum no documento
  // (nem seleção, nem edição) — só volta a ser editável ao destrancar. Sem isso, um
  // fundo full-bleed capturaria todo clique na página.
  const bloqueado = !!elemento.bloqueado;

  return (
    <div
      ref={node => registerRef(elemento.el_id, node)}
      data-elid={elemento.el_id}
      className={`absolute ${bloqueado || editing ? '' : 'cursor-move'} ${bloqueado || (selected && !editing) ? '' : 'hover:outline hover:outline-1 hover:outline-violet-300'}`}
      style={{
        left: elemento.x,
        top: elemento.y,
        width: elemento.largura,
        height: elemento.altura,
        transform: elemento.rotacao ? `rotate(${elemento.rotacao}deg)` : undefined,
        zIndex: elemento.z || 0,
        pointerEvents: bloqueado ? 'none' : undefined,
        // Selecionada pelo painel de camadas: contorno tracejado indicando "trancada"
        // (não há alças do Moveable para arrastar/redimensionar).
        outline: bloqueado && selected ? '1px dashed #9ca3af' : undefined,
      }}
      onMouseDown={e => {
        if (!editing) {
          e.stopPropagation();
          // O evento vai junto: Shift/Ctrl+clique soma o elemento à seleção
          onSelect(e);
        }
      }}
      onDoubleClick={e => {
        if (isTexto && !editing) {
          e.stopPropagation();
          onStartEdit();
        }
      }}
    >
      {editing && isTexto ? (
        <div
          ref={editableRef}
          contentEditable
          suppressContentEditableWarning
          className="w-full h-full overflow-hidden break-words outline outline-2 outline-[#7C3AED] rounded-sm bg-white/60 [&_p]:m-0"
          style={{
            fontSize: estilo.fontSize || 14,
            color: estilo.color || '#374151',
            textAlign: estilo.textAlign || 'left',
            fontWeight: estilo.fontWeight || 'normal',
            fontStyle: estilo.fontStyle || 'normal',
            lineHeight: estilo.lineHeight || 1.6,
            cursor: 'text',
          }}
          onBlur={() => onCommitHtml(editableRef.current?.innerHTML || '')}
          onMouseDown={e => e.stopPropagation()}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              e.preventDefault();
              editableRef.current?.blur();
            }
            e.stopPropagation();
          }}
        />
      ) : (
        <CanvasElementContent elemento={elemento} proposta={proposta} resolver={false} />
      )}
    </div>
  );
}
